import { ALLOCATION_BAG_ID, ItemFields, ITEMS_ID, Kinds, TARGET_BAG_ID } from "constants/product-collection";
import { List, Map } from "immutable";
import get from "lodash/get";

// Collection Helper Methods
interface IAdjustDestinationIndexParams {
    source: number; // The index a row starts at
    proposedDestination: number; // The potential destination index for the row
    totalLength: number; // The total length of the collection
}

/**
 * This method is used to adjust the destination of a given destination index given a start index and list length.
 *
 * - We need this function because in Operational Transformation, a "move" operation is a "remove", then an "insert".
 * - The indices change between removal and insertion, so we must adjust our destination index accordingly.
 */
export const adjustDestinationIndex = ({
    source,
    proposedDestination,
    totalLength,
}: IAdjustDestinationIndexParams): number => {
    let destination: number;
    if (proposedDestination === -1) {
        destination = totalLength;
    } else if (proposedDestination > source) {
        destination = proposedDestination + 1;
    } else {
        destination = proposedDestination;
    }
    // if either destination or totalLength are undefined, then Math.min() returns NaN
    // that's why we fall back to the proposedDestination
    return Math.min(destination, totalLength) || proposedDestination;
};

export interface IGridData {
    data: List<any>;
    aggData: Map<string, Map<string, any>>;
    groupHeaders: string[];
}

export const NODE = "_node";

export const getFieldValue = (data: object, key: string, defaultValue: any = null) => {
    const fieldValue = get(data, ["fields", key, "string_value"], "");
    if (!fieldValue) {
        return defaultValue;
    }

    // Fe_lookup_type values store the value as "<ID>; <Description>"
    // Question: Do we need a setting like field_value_delimiter or the delimiter is always ";"?
    if (fieldValue.includes(";")) {
        return fieldValue.split(";")[0].trim();
    }

    return fieldValue;
};

export const getBrandNameForItem = (itemDisplayProperty: List<any>, item: Record<any, any>) => {
    const fieldName = itemDisplayProperty?.find((field) => field?.get("kind") === Kinds.BRAND_NAME)?.get("field_name");
    return item && fieldName && item.getIn([ItemFields.Fields, fieldName, "string_value"]);
};

export const getMutationBatchSizeByObjectId = (objectId: string): number => {
    let batchMaxSize = -Infinity;
    if (objectId === ITEMS_ID) {
        batchMaxSize = 25;
    } else if (objectId === ALLOCATION_BAG_ID) {
        batchMaxSize = 1000;
    } else if (objectId === TARGET_BAG_ID) {
        batchMaxSize = 1000;
    }

    return batchMaxSize;
};
